/**
 * `entities/analysis` read API.
 *
 * Thin wrappers around the generated `AnalysisService` read endpoints. Write
 * operations (create/retry/delete) deliberately stay in `features/analysis` —
 * the entity owns reads only.
 */
import {
  AnalysisService,
  type WorkspaceResolutionResponseDto,
} from '@inference.engine/api-client/web';
import { inferenceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { downloadPdfBlob } from '@/shared/api/pdf-download.api';
import type { Analysis, AnalysisListItem } from '../model/types';
import { parseStatistaDatasets, type StatistaDataset } from '../model/statista.schema';

export const getAnalyses = async (signal?: AbortSignal): Promise<AnalysisListItem[]> => {
  const response = await AnalysisService.listSessions({
    client: inferenceHttp,
    signal,
  });

  if (!response.data) {
    throw new Error('No data returned from analysis API');
  }

  return response.data.data;
};

export type GetAnalysisByIdOptions = {
  signal?: AbortSignal;
  suppressErrorNotification?: boolean;
};

export const getAnalysisById = async (
  id: string,
  options?: GetAnalysisByIdOptions,
): Promise<Analysis> => {
  const response = await AnalysisService.getAnalysis({
    client: inferenceHttp,
    path: { id },
    signal: options?.signal,
    // When the resolution seam is active it owns the not-found UX, so a
    // cross-workspace 404 must not flash the global toast. Opt-in only:
    // every other consumer keeps the pre-seam toast behavior.
    headers: options?.suppressErrorNotification
      ? // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
        { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' }
      : undefined,
  });

  if (!response.data) {
    throw new Error('No data returned from analysis API');
  }

  return response.data;
};

export const resolveAnalysis = async (id: string): Promise<WorkspaceResolutionResponseDto> => {
  const response = await AnalysisService.resolveWorkspace({
    client: inferenceHttp,
    path: { id },
    // The resolution seam maps a 404 to its own generic "not available"
    // state; the global toast would leak the raw error first.
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to resolve analysis');
  }

  return response.data;
};

/**
 * Fetch an analysis for the `/download/iq-analysis/:id` print page. The core API
 * scopes by workspace and the headless render has no store, so the workspace
 * (from the print page's `?workspaceId=`) is passed as the header explicitly —
 * same pattern as `getFocusGroupForDownload`.
 */
export const getAnalysisForDownload = async (
  workspaceId: string,
  id: string,
): Promise<Analysis> => {
  const response = await AnalysisService.getAnalysis({
    client: inferenceHttp,
    path: { id },
    headers: { 'x-selected-workspace-id': workspaceId },
  });

  if (!response.data) {
    throw new Error('No data returned from analysis API');
  }

  return response.data;
};

/**
 * Statista datasets backing the Category & Competition figures. The backend
 * already scopes to Block B and to the caller's workspace, so the response is
 * rendered in the order it arrives rather than filtered again here.
 *
 * Parsed through `parseStatistaDatasets` because the generated types are
 * unusable for the fields that matter — see `model/statista.schema.ts`.
 */
export const getStatistaDatasets = async (
  id: string,
  signal?: AbortSignal,
): Promise<StatistaDataset[]> => {
  const response = await AnalysisService.getStatistaDatasets({
    client: inferenceHttp,
    path: { id },
    signal,
    // The charts are an enhancement to a report that reads fine without them, and the
    // section already renders the empty case. A failure here should not put raw backend
    // text in a toast the reader has to dismiss by hand.
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  return parseStatistaDatasets(response.data);
};

/** Download one section of an analysis as a server-rendered PDF blob. The
 *  backend scopes the render to the caller's own workspace, so only `section`
 *  is forwarded. */
export const downloadAnalysisPdf = (id: string, { section }: { section: string }): Promise<Blob> =>
  // eslint-disable-next-line i18next/no-literal-string -- interpolated into the diagnostic `pdf-download.api.ts` throws; every caller toasts its own translated message
  downloadPdfBlob('analysis', id, { label: 'Analysis', params: { section } });
